import {
  Catalogue,
  CatalogueInstance,
  CatalogueQuestion,
  findInstance,
  findQuestion,
} from "../types/catalogue"
import { Banner, BannerGroup, BannerPoint, isConditionGroup } from "../types/tabSpecDocument"
import {
  boundsProblem,
  conditionExpression,
  conditionRefusal,
  NUMBER_TYPES,
  rangeExpression,
} from "../model/conditions"
import { RAW_NAMES, ShownNames } from "../model/shownLabels"
import {
  bannerTableName,
  inLoadedData,
  ref,
  refusedName,
  TOTAL_TABLE,
  unsafeLabel,
  unsafeRef,
  unsafeText,
} from "./names"

export interface BannerColumn {
  /** The group header, as the engine returns it. */
  group: string
  /** The column label, as the engine returns it. */
  label: string
  /** What the table shows. */
  display: string
  /**
   * The heading the table shows: the group's own heading, or the variable's
   * shown label, numbered like `group`. Equal to `group` without shown labels.
   */
  groupDisplay: string
}

export interface BannerPlan {
  /** The engine table to use as the column variable. `TOTAL_TABLE` when no group is usable. */
  name: string
  /** The script that creates it; null when there is nothing to create. */
  script: string | null
  columns: BannerColumn[]
  /** What was left out, and why. Shown in the banner editor and on the tables that use the banner. */
  problems: string[]
}

interface ResolvedPoint {
  label: string
  display: string
  paths: string[]
  net: boolean
  /** A range or a condition column: the expression its field is worked out from. null for a category or a net. */
  calc: string | null
}
interface ResolvedGroup {
  header: string
  /** What the heading shows before numbering; null = the engine's `header` itself. */
  shown: string | null
  points: ResolvedPoint[]
}

/** Why an option of a point could not be used. */
type Loss = "gone" | "unsafe" | "not-loaded"
const LOSS: Record<Loss, string> = {
  gone: "no longer exists",
  unsafe: "has a character the engine cannot take",
  "not-loaded": "is not in the loaded data",
}

/**
 * What a banner group stands on once it can be used: its question and the
 * instance whose paths its columns use (none for a group of conditions),
 * whether that is a number, and its heading before numbering.
 */
export interface GroupBasis {
  /** null for a group of conditions. */
  question: CatalogueQuestion | null
  /** The question's only instance, or the grid row or loop iteration the group names; null for a group of conditions. */
  instance: CatalogueInstance | null
  /** The variable is a number: its columns are ranges. */
  number: boolean
  /** The engine heading before numbering: the group's own, or the question (and its row) in words. */
  heading: string
  /** What the heading shows before numbering; null = `heading` itself. */
  shown: string | null
  /** How the sentences name the group: the variable as shown, with its row; a group of conditions by its heading. */
  named: string
  /** What the sentences say of the group without leaving it out. */
  notes: string[]
}

/** Why a heading cannot be one, or null when it can. */
const headingProblem = (heading: string): string | null => {
  if (unsafeLabel(heading)) return `The banner heading "${heading}" has a character the engine cannot take.`
  // The rule of the shown names, as for the columns. Headings are not
  // compared with each other: a second one is numbered.
  const refused = refusedName(heading, [], "column")
  return refused ? `The banner heading "${heading}" was left out with its columns. ${refused}` : null
}

/**
 * What `g` stands on (`GroupBasis`), or the sentence that says why it is left
 * out of the banner. The one rule of `compileBanner` and of the editor's
 * headings (`groupHeadings`). A question asked more than once (a grid, a
 * loop) needs the instance the group names; a number needs ranges; any other
 * question needs answers. Once data is loaded, the instance must be in it.
 */
export const groupBasis = (
  g: BannerGroup,
  catalogue: Catalogue,
  names: ShownNames = RAW_NAMES
): GroupBasis | string => {
  if (isConditionGroup(g)) {
    const heading = (g.label ?? "").trim()
    if (!heading) return "A group of conditions needs a heading, so it was left out of the banner."
    const problem = headingProblem(heading)
    if (problem) return problem
    return { question: null, instance: null, number: false, heading, shown: null, named: heading, notes: [] }
  }
  const q = findQuestion(catalogue, g.variable, g.source)
  if (!q) {
    const variable = names.variable(g.variable)
    return g.source !== "data"
      ? `${variable} is not in the questionnaire, so it was left out of the banner.`
      : findQuestion(catalogue, g.variable)
        ? `${variable} is a data variable, but the questionnaire now has a question with that name, so it was left out of the banner.`
        : catalogue.knownPaths
          ? `${variable} is not in the prepared data, so it was left out of the banner.`
          : `${variable} comes from the data, which is not loaded yet, so it was left out of the banner for now.`
  }
  const variable = names.variable(q.label)
  let instance: CatalogueInstance
  /** The grid row or loop iteration in its own words; null for a question asked once. */
  let row: string | null = null
  /** The same as the tables show it. */
  let rowShown: string | null = null
  if (g.instance !== undefined) {
    const found = findInstance(q, g.instance)
    if (!found)
      return `${variable} no longer has "${names.instance(q.label, g.instance, g.instance)}", so it was left out of the banner.`
    instance = found
    row = found.label || found.id
    rowShown = names.instance(q.label, found.id, row)
  } else if (q.instances.length !== 1) {
    // A grid of the data was never asked: it is a grid, and says so.
    return q.source === "data"
      ? `${variable} cannot be used in a banner: a grid cannot be a banner group.`
      : `${variable} cannot be used in a banner: it is asked per row or per loop.`
  } else instance = q.instances[0]
  const named = rowShown === null ? variable : `${variable} - ${rowShown}`
  const number = NUMBER_TYPES.includes(q.type)
  if (number) {
    // Its columns are ranges (or conditions); every answer means nothing here.
    const points = g.points ?? []
    if (!points.some((p) => p.kind === "range" || p.kind === "condition"))
      return `${named} is a number: choose ranges for its columns.`
    if (unsafeText(instance.path) || unsafeRef(instance.path))
      return `${named} has a character the engine cannot take, so it was left out of the banner.`
  } else if (Object.keys(instance.optionPaths).length === 0)
    return `${named} cannot be used in a banner: it is not a single or multiple choice question.`
  if (catalogue.knownPaths && !instance.inData)
    return `${named} is not in the loaded data, so it was left out of the banner.`
  // The engine heading: the group's own, or the raw label (and the row's
  // own label). A variable of the data nested in the codebook is named by
  // its path; the heading is one segment, so its parts are joined with ": "
  // ("Channels: Bank 1").
  const own = (g.label ?? "").trim()
  const base = q.label.split("//").join(": ")
  const heading = own || (row === null ? base : `${base} - ${row}`)
  const problem = headingProblem(heading)
  if (problem) return problem
  // The shown heading: the group's own first, then the variable's (and the
  // row's) shown label; a shown label that cannot be one gives way to the
  // raw label.
  const notes: string[] = []
  let shown: string | null = null
  const text = row === null ? variable : `${variable === q.label ? base : variable} - ${rowShown}`
  if (!own && (row === null ? variable !== q.label : text !== heading)) {
    const refusedShown = refusedName(text, [], "column", "display")
    if (refusedShown)
      notes.push(`The shown label "${text}" of ${q.label} was not used as the banner heading. ${refusedShown}`)
    else shown = text
  }
  return { question: q, instance, number, heading, shown, named, notes }
}

/**
 * The shown headings of `groups`, in order. A group that shows its engine
 * header (its own heading, or a variable without a shown label) shows it
 * exactly; a shown label is numbered, as the engine headers are, among every
 * heading the banner shows.
 */
const groupDisplays = (groups: ResolvedGroup[]): string[] => {
  const taken = new Set(groups.flatMap((g) => (g.shown === null ? [g.header] : [])))
  return groups.map((g) => {
    if (g.shown === null) return g.header
    let display = g.shown
    for (let n = 2; taken.has(display); n++) display = `${g.shown} (${n})`
    taken.add(display)
    return display
  })
}

/**
 * `names`: the shown labels the headings, the columns and the sentences use.
 * They never reach the engine: the banner's script and name come from the
 * raw labels only.
 *
 * A group is a question (a grid row or loop iteration of one when it names
 * the instance) with its answers and nets as columns, a number with range
 * columns, or a group of conditions with a heading of its own. A range or a
 * condition column is a yes or no field worked out by a CALC; it is missing
 * where its number or its condition is, and the closing BLANKFALSE makes a
 * missing value "not in this column", as for every other column.
 */
export const compileBanner = (
  banner: Banner,
  catalogue: Catalogue,
  names: ShownNames = RAW_NAMES
): BannerPlan => {
  const problems: string[] = []
  const headers = new Set<string>()
  const groups: ResolvedGroup[] = []

  banner.groups.forEach((g) => {
    const basis = groupBasis(g, catalogue, names)
    if (typeof basis === "string") {
      problems.push(basis)
      return
    }
    const { question: q, instance, named } = basis
    let header = basis.heading
    for (let n = 2; headers.has(header); n++) header = `${basis.heading} (${n})`
    headers.add(header)
    problems.push(...basis.notes)

    const optionPaths = instance?.optionPaths ?? {}
    /** An answer as the sentences name it: by its shown label. */
    const optionName = (label: string): string => (q ? names.option(q.label, label) : label)
    const wanted: BannerPoint[] =
      g.points ?? (q?.options ?? []).map((o) => ({ kind: "category" as const, label: o.label }))
    const used = new Set<string>()
    /** The names the columns kept so far are shown under. */
    const taken: string[] = []
    /**
     * The names a shown label gives way to, wherever they stand: the raw
     * label and the rename of each category, and the name of every other
     * column. A display name never costs a column its place.
     */
    const pointNames = wanted.flatMap((p): { label: string | null; name: string }[] => {
      if (p.kind !== "category") return [{ label: null, name: p.name.trim() }]
      const rename = p.rename?.trim()
      return [p.label, ...(rename ? [rename] : [])].map((name) => ({ label: p.label, name }))
    })
    const points: ResolvedPoint[] = []
    /** A range or a condition column: its expression, or null once the sentence that leaves it out is said. */
    const calcOf = (p: Extract<BannerPoint, { kind: "range" | "condition" }>, label: string): string | null => {
      if (p.kind === "range") {
        if (!basis.number || !instance) {
          problems.push(`${named} is not a number, so the range "${label}" was left out.`)
          return null
        }
        const problem = boundsProblem(p.min, p.max)
        if (problem) {
          problems.push(`${named}: the range "${label}" ${problem} and was left out.`)
          return null
        }
        return rangeExpression(instance.path, p.min, p.max)
      }
      const refusal = conditionRefusal(p.condition, catalogue, names, "condition")
      if (refusal) {
        problems.push(`${named}: "${label}" was left out. ${refusal}`)
        return null
      }
      const expression = conditionExpression(p.condition)
      if (expression === null) problems.push(`${named}: "${label}" has no condition yet, so it was left out.`)
      return expression
    }
    wanted.forEach((p) => {
      if (p.kind === "range" || p.kind === "condition") {
        const label = p.name.trim()
        if (used.has(label)) {
          problems.push(`${named}: "${label}" is used twice, so the second was left out.`)
          return
        }
        const refused = refusedName(label, taken, "column")
        if (refused) {
          problems.push(`${named}: "${label}" was left out. ${refused}`)
          return
        }
        const calc = calcOf(p, label)
        if (calc === null) return
        used.add(label)
        taken.push(label)
        points.push({ label, display: label, paths: [], net: false, calc })
        return
      }
      // A category keeps the option label: the engine names the appended leaf by it.
      const label = p.kind === "category" ? p.label : p.name.trim()
      /** The point as the sentences name it: a category by its shown label. */
      const point = p.kind === "category" ? optionName(label) : label
      if (unsafeLabel(label)) {
        problems.push(
          p.kind === "net"
            ? `${named}: the net name "${label}" cannot be used, so the net was left out.`
            : `${named}: "${point}" has a character the engine cannot take, so it was left out.`
        )
        return
      }
      const paths: string[] = []
      const lost: { member: string; why: Loss }[] = []
      const members = p.kind === "category" ? [p.label] : p.members
      members.forEach((member) => {
        const path = optionPaths[member]
        if (!path) lost.push({ member, why: "gone" })
        else if (unsafeText(path)) lost.push({ member, why: "unsafe" })
        else if (!inLoadedData(catalogue, path)) lost.push({ member, why: "not-loaded" })
        else paths.push(path)
      })
      if (paths.length === 0) {
        const why = lost.some((l) => l.why === "unsafe")
          ? "has a character the engine cannot take"
          : lost.some((l) => l.why === "not-loaded")
            ? "is not in the loaded data"
            : "has no option that still exists"
        problems.push(`${named}: "${point}" ${why}, so it was left out.`)
        return
      }
      if (used.has(label)) {
        problems.push(`${named}: "${point}" is used twice, so the second was left out.`)
        return
      }
      // A rename first, then the shown label, then the raw label. A shown
      // label that cannot be used gives way to the raw label; a rename that
      // cannot be used leaves the column out, as the raw label does.
      const rename = p.kind === "category" ? p.rename?.trim() : undefined
      let display = rename || label
      let shownUsed = false
      if (!rename && point !== label) {
        const others = [
          ...taken,
          ...pointNames.filter((c) => c.label !== label).map((c) => c.name),
        ]
        const refusedShown = refusedName(point, others, "column", "display")
        if (refusedShown)
          problems.push(`${named}: the shown label "${point}" of "${label}" was not used. ${refusedShown}`)
        else {
          display = point
          shownUsed = true
        }
      }
      if (!shownUsed) {
        const refused = refusedName(display, taken, "column")
        if (refused) {
          problems.push(`${named}: "${rename ? point : label}" was left out. ${refused}`)
          return
        }
      }
      used.add(label)
      taken.push(display)
      // Only a net can get here with a loss: it is built from the members that are left.
      lost.forEach(({ member, why }) =>
        problems.push(
          `${named}: "${optionName(member)}" ${LOSS[why]}, so the net "${label}" was built without it.`
        )
      )
      points.push({ label, display, paths, net: p.kind === "net", calc: null })
    })
    if (points.length > 0) groups.push({ header, shown: basis.shown, points })
  })

  if (groups.length === 0)
    return { name: TOTAL_TABLE, script: null, columns: [], problems }

  // A category or a net as before, so a banner of them keeps its name; a
  // range or a condition by its expression, which holds its bounds or its
  // condition and the path it reads.
  const name = bannerTableName(
    groups.map((g) => ({
      h: g.header,
      p: g.points.map((p) => (p.calc === null ? [p.label, p.paths, p.net] : [p.label, p.calc])),
    }))
  )
  const lines = [`QTABLE ${name}`]
  groups.forEach((g) => {
    const group = `${name}//${g.header}`
    lines.push(`QTABLE ${ref(group)}`)
    g.points.forEach((p) => {
      const field = ref(`${group}//${p.label}`)
      if (p.calc !== null) {
        lines.push(`FIELD ${field} BOOL`, `CALC ${field} "${p.calc}"`)
        return
      }
      if (!p.net) {
        lines.push(`APPEND ${ref(group)} ${ref(p.paths[0])}`)
        return
      }
      lines.push(`FIELD ${field} BOOL false`)
      p.paths.forEach((path) => lines.push(`OR ${field} ${ref(path)}`))
    })
  })
  lines.push(`BLANKFALSE ${name}`)

  const displays = groupDisplays(groups)
  return {
    name,
    script: lines.join("\n"),
    columns: groups.flatMap((g, i) =>
      g.points.map((p) => ({
        group: g.header,
        label: p.label,
        display: p.display,
        groupDisplay: displays[i],
      }))
    ),
    problems,
  }
}
